import { defineComponent as _defineComponent } from 'vue'
import { storeToRefs } from "pinia"
import { computed, getCurrentInstance, nextTick, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router/composables"

import { conditions } from "@/consts/offerConditions"
import synchronizationFormats from "@/consts/synchronizationFormats"
import i18n from "@/i18n"
import { useSyncMetaStore } from "@/modules/sync/store/useSyncMetaStore"
import { createSyncPayload, useSyncStore } from "@/modules/sync/store/useSyncStore"
import syncService from "@/services/syncService"
import setFormErrors from "@/utils/setFormErrors"

import { useSyncOptions } from "../useSyncOptions"

// Helper Types/Variables

export default /*#__PURE__*/_defineComponent({
  __name: 'SyncSettings',
  props: {
  editMode: {
    type: Boolean,
    default: false
  }
},
  setup(__props) {

const props = __props;

const VNodes = {
  functional: true,
  render: (h, ctx) => ctx.props.vnodes
}



// End Helper Types/Variables

// Store & Route
const syncMetaStore = useSyncMetaStore()
const syncStore = useSyncStore()
const router = useRouter()
const route = useRoute()

const { proxy } = getCurrentInstance()
const { synchronization, fetching: syncFetching } = storeToRefs(syncStore)
const { deliveryPolitics, fetchingPolitics } = storeToRefs(syncMetaStore)
// End Store & Route

// State
const xmlNotVerificated = ref(true)
const fetching = ref(false)
const xmlChecking = ref(false)
const errors = ref("")
const editPrices = ref("")
const discountPrecision = ref(2)

const form = proxy.$form.createForm(proxy)
// End State

// Composables
const { uploadProductsOptions, updateProductOptions, editPricesOptions } = useSyncOptions()
// End Composables

// Computed
const deliveryPoliticsOptions = computed(() => {
  return deliveryPolitics.value.map((item) => ({
    label: item.name,
    value: item.uuid
  }))
})
const hasDeliveries = computed(() => deliveryPolitics.value.length > 0)
const offerConditionsOptions = computed(() => {
  return conditions.map((state) => {
    return {
      label: state.description,
      value: state.backKey + "-" + state.backValue
    }
  })
})
const disableForm = computed(() => {
  return xmlNotVerificated.value || !hasDeliveries.value
})
const maxEditPricePercent = computed(() => {
  return editPrices.value === "ascend" ? 999 : 90
})
// End Computed

// Methods
const resetForm = () => {
  fillForm()
}
const fillForm = () => {
  form.setFields({
    url: {
      value: synchronization.value.url,
      errors: null
    },
    format: {
      value: synchronization.value.format,
      errors: null
    },
    load_only: {
      value: synchronization.value.load_only,
      errors: null
    },
    frequency: {
      value: synchronization.value.frequency,
      errors: null
    },
    delivery: {
      value: synchronization.value.delivery?.uuid,
      errors: null
    },
    state: {
      value: `${synchronization.value.condition_key}-${synchronization.value.condition_value}`,
      errors: null
    },
    warranty: {
      value: synchronization.value.warranty,
      errors: null
    },
    discount_percent: {
      value: synchronization.value.discount_percent,
      errors: null
    }
  })

  if (!synchronization.value.discount_percent) {
    editPrices.value = ""
  } else if (synchronization.value.discount_percent > 0) {
    editPrices.value = "ascend"
  } else {
    editPrices.value = "descend"
  }
}
const setXmlNotVerificated = () => {
  xmlNotVerificated.value = true
}
const handleCheckXmlFile = async () => {
  let data = form.getFieldsValue(["url", "format"])

  // Reset url field errors
  form.setFields({
    url: {
      value: data.url,
      errors: null
    }
  })
  errors.value = ""

  if (data.url !== "") {
    xmlChecking.value = true

    try {
      await syncService.verifySynchronization(data)
      xmlNotVerificated.value = false
    } catch (err) {
      if (err?.response?.data?.url) {
        form.setFields(setFormErrors({ url: err.response.data.url }, data))
        setXmlNotVerificated()
      } else if (err?.response?.data?.xml_created_at) {
        errors.value = Array.isArray(err.response.data.xml_created_at)
          ? err.response.data.xml_created_at.join(", ")
          : err.response.data.xml_created_at
      } else if (err?.response?.data["yml_catalog.date"]) {
        errors.value = Array.isArray(err.response.data["yml_catalog.date"])
          ? err.response.data["yml_catalog.date"].join(", ")
          : err.response.data["yml_catalog.date"]
      } else {
        errors.value =
          i18n.t("synchronization.emptyFieldsError") +
          Object.keys(err?.response?.data || {}).join(", ")
      }
    } finally {
      xmlChecking.value = false
    }
  } else {
    form.setFields(
      setFormErrors({
        url: [i18n.t("validations.required_field")]
      })
    )
  }
}
const createOrUpdateSynchronization = async (values) => {
  fetching.value = true

  const { state, discount_percent = 0, warranty, ...data } = values
  const [backKey, backValue] = state.split("-")
  const payload: createSyncPayload = {
    condition_key: backKey,
    condition_value: backValue ? backValue : 1,
    discount_percent: editPrices.value === "descend" ? -discount_percent : discount_percent,
    warranty: Number(warranty)?.toFixed() || 0,
    ...data
  }

  if (props.editMode) {
    syncService
      .updateSynchronization({
        data: payload,
        uuid: synchronization.value.uuid
      })
      .then(() => {
        xmlNotVerificated.value = true
      })
      .catch((err) => {
        form.setFields(setFormErrors(err.response?.data, form.getFieldsValue()))

        if (err.response?.data.details) {
        }

        throw err
      })
      .finally(() => {
        fetching.value = false
      })
  } else {
    syncService
      .createSynchronization(payload)
      .then(() => {
        router.push({ name: "sync-list" })
        xmlNotVerificated.value = true
      })
      .catch((err) => {
        form.setFields(setFormErrors(err.response?.data, form.getFieldsValue()))
        throw err
      })
      .finally(() => {
        fetching.value = false
      })
  }
}
const handleSubmit = (e) => {
  e.preventDefault()

  form.validateFields((err, values) => {
    if (!err) {
      createOrUpdateSynchronization(values)
    }
  })
}
const onEditPriceOptionChange = (value) => {
  if (!value) {
    form.setFields({ discount_percent: { value: 0, errors: null } })
  }
}
const handleCreateDelivery = () => {
  router.push({
    name: "delivery",
    query: {
      openAddPolicyModal: "true"
    }
  })
}
// End Methods

// Lifecycle
onMounted(() => {
  syncMetaStore.getAllActiveDeliveries()

  // Short delay to ensure form is ready if sync?
  if (props.editMode) {
    syncStore.getSynchronization(route.params.uuid).then(() => {
      fillForm()
      handleCheckXmlFile()
    })
  }
})
// End Lifecycle

return { __sfc: true,VNodes, props, syncMetaStore, syncStore, router, route, proxy, synchronization, syncFetching, deliveryPolitics, fetchingPolitics, xmlNotVerificated, fetching, xmlChecking, errors, editPrices, discountPrecision, form, uploadProductsOptions, updateProductOptions, editPricesOptions, deliveryPoliticsOptions, hasDeliveries, offerConditionsOptions, disableForm, maxEditPricePercent, resetForm, fillForm, setXmlNotVerificated, handleCheckXmlFile, createOrUpdateSynchronization, handleSubmit, onEditPriceOptionChange, handleCreateDelivery, synchronizationFormats }
}

})